/**
 * Boot-time validation of Supabase tokens stored in localStorage.
 *
 * The Supabase JS SDK stores its session under a key like `sb-<ref>-auth-token`.
 * If that token is malformed or its refresh_token is expired, the SDK will
 * make a `/token?grant_type=refresh_token` call at boot. When that call
 * times out (we've observed `500: error finding refresh token: timeout`),
 * the entire app hangs until our safety timeout fires, producing the
 * "white screen / spinner forever" behavior reported on refresh.
 *
 * This helper inspects the stored token BEFORE Supabase boots and removes
 * it if it's clearly unusable. The next createClient() call then proceeds
 * with a clean state and the user simply lands on the public route.
 */

import { clearAllAuthStorage } from "@/lib/storage/cleanup";

const SB_TOKEN_PREFIX = "sb-";
const SB_TOKEN_SUFFIX = "-auth-token";

/** Decode the JWT payload safely. Returns null if not a JWT. */
const decodeJwtPayload = (jwt: string): { exp?: number } | null => {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload;
  } catch {
    return null;
  }
};

/**
 * Validate the stored Supabase session.
 * Returns true if the token looks usable; false if we removed it.
 */
export const validateStoredSupabaseSession = (): boolean => {
  if (typeof window === "undefined") return true;

  try {
    const storage = window.localStorage;
    let tokenKey: string | null = null;

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(SB_TOKEN_PREFIX) && key.endsWith(SB_TOKEN_SUFFIX)) {
        tokenKey = key;
        break;
      }
    }

    if (!tokenKey) return true; // no session, nothing to validate

    const raw = storage.getItem(tokenKey);
    if (!raw) return true;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      console.warn("[bootCleanup] Supabase token is malformed JSON — clearing");
      clearAllAuthStorage();
      return false;
    }

    const session = parsed as {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number;
    };

    // Missing critical fields → clear
    if (!session?.access_token || !session?.refresh_token) {
      console.warn("[bootCleanup] Supabase token missing fields — clearing");
      clearAllAuthStorage();
      return false;
    }

    // If access_token is a JWT, check exp. We do NOT clear when only the
    // access_token is expired — the SDK can refresh it. We only clear when
    // the access_token is so old that its refresh window is clearly past
    // (heuristic: > 30 days old).
    const payload = decodeJwtPayload(session.access_token);
    if (payload?.exp) {
      const ageDays = (Date.now() / 1000 - payload.exp) / 86400;
      if (ageDays > 30) {
        console.warn(
          "[bootCleanup] Supabase token expired > 30d ago — clearing",
          { ageDays: Math.round(ageDays) }
        );
        clearAllAuthStorage();
        return false;
      }
    }

    return true;
  } catch (err) {
    // An unexpected exception here (e.g. localStorage unavailable in private
    // mode, quota exceeded, security policy blocking access) tells us
    // nothing about the validity of the stored token. Wiping the session
    // in this case caused random logouts on private-mode browsers and
    // storage-restricted environments. Leave the token alone and let the
    // Supabase SDK do its own validation — it can refresh, recover, or
    // surface a real auth error to the app.
    console.warn("[bootCleanup] unexpected error during validation, leaving storage intact", err);
    return true;
  }
};
